import React, { useEffect, useRef } from 'react';
import QRCodeLib from 'qrcode';

/**
 * QR Code version. If not specified the most suitable value will be calculated.
 */
type QRCodeVersion = number;

/**
 * Error correction level for the QR code.
 * - 'L' or 'low': 7% of codewords can be restored
 * - 'M' or 'medium': 15% of codewords can be restored
 * - 'Q' or 'quartile': 25% of codewords can be restored
 * - 'H' or 'high': 30% of codewords can be restored
 */
type QRCodeErrorCorrectionLevel = 'L' | 'M' | 'Q' | 'H' | 'low' | 'medium' | 'quartile' | 'high';

/**
 * Mask pattern used to mask the symbol.
 */
type MaskPattern = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7;

/**
 * QR Code options for rendering
 */
interface QRCodeRenderOptions {
  /**
   * QR Code version. If not specified the most suitable value will be calculated.
   */
  version?: QRCodeVersion;
  
  /**
   * Error correction level. Default is 'M'.
   */
  errorCorrectionLevel?: QRCodeErrorCorrectionLevel;
  
  /**
   * Mask pattern used to mask the symbol.
   * If not specified the most suitable value will be calculated.
   */
  maskPattern?: MaskPattern;
  
  /**
   * Helper function used internally to convert a kanji to its Shift JIS value.
   * Provide this function if you need support for Kanji mode.
   */
  toSJISFunc?: (kanji: string) => number;
  
  /**
   * Define how much wide the quiet zone should be. Default is 4.
   */
  margin?: number;
  
  /**
   * Scale factor. A value of 1 means 1px per modules (black dots). Default is 4.
   */
  scale?: number;
  
  /**
   * Forces a specific width for the output image.
   * If width is too small to contain the qr symbol, this option will be ignored.
   * Takes precedence over scale.
   */
  width?: number;
  
  /**
   * Color options for the QR code
   */
  color?: {
    /**
     * Color of dark module. Value must be in hex format (RGBA).
     * Default is #000000ff (black)
     */
    dark?: string;
    
    /**
     * Color of light module. Value must be in hex format (RGBA).
     * Default is #ffffffff (white)
     */
    light?: string;
  };
}

/**
 * Props for the QRCode component
 */
interface QRCodeProps {
  /**
   * The text or URL to encode in the QR code
   */
  value: string;
  
  /**
   * Options for rendering the QR code
   */
  options?: QRCodeRenderOptions;
  
  /**
   * Optional CSS class name for the canvas element
   */
  className?: string;
  
  /**
   * Optional inline style for the canvas element
   */
  style?: React.CSSProperties;
  
  /**
   * Optional callback function called when the QR code is generated
   */
  onGenerated?: () => void;
  
  /**
   * Optional callback function called when an error occurs during QR code generation
   */
  onError?: (error: Error) => void;
}

/**
 * QRCode Component
 * 
 * A React component that renders a QR code using the qrcode library.
 * Supports all options from the qrcode package and provides additional styling capabilities.
 * 
 * @example
 * // Basic usage
 * <QRCode value="https://example.com" />
 * 
 * @example
 * // With custom options
 * <QRCode 
 *   value="https://example.com"
 *   options={{
 *     errorCorrectionLevel: 'H',
 *     margin: 2,
 *     scale: 8,
 *     color: {
 *       dark: '#010599FF',
 *       light: '#FFBF60FF'
 *     }
 *   }}
 *   className="my-qrcode"
 *   onGenerated={() => console.log('QR code generated!')}
 * />
 */
const QRCode: React.FC<QRCodeProps> = ({ 
  value, 
  options = {}, 
  className, 
  style,
  onGenerated,
  onError
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (canvasRef.current && value) {
      // Default options with high error correction
      const defaultOptions = { errorCorrectionLevel: 'H' };
      
      // Merge default options with user-provided options
      const mergedOptions = { ...defaultOptions, ...options };
      
      QRCodeLib.toCanvas(
        canvasRef.current,
        value,
        mergedOptions,
        (err: Error | null | undefined) => {
          if (err) {
            console.error('QR Code generation error:', err);
            onError?.(err);
          } else {
            onGenerated?.();
          }
        }
      );
    }
  }, [value, options, onGenerated, onError]);

  return <canvas ref={canvasRef} className={className} style={style} />;
};

export default QRCode;

